import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth';
import { createVessel, getCompanyAdminList, getCompanyList, uploadVesselImage } from '../core/_requests';
import { Company, CompanyAdmin } from '../core/_models';
import { toast } from 'react-toastify';
import { KTSVG } from '../../../../_metronic/helpers';
import {VESSEL_TYPE_OPTIONS} from '../core/vesselTypeOptions';
import VesselContactListField from './VesselContactListField';
import {
  resolveVesselCreateOwnership,
  resolveVesselCreatePayloadIds,
} from '../core/vesselCreateOwnership';
import {
  addContactEntry,
  buildContactEntries,
  getPrimaryContactValue,
  removeContactEntry,
  serializeContactEntries,
  setPrimaryContact,
  updateContactValue,
  validateContactEntries,
} from '../core/vesselContactFields';
import SearchableSelect from '../../../shared/components/SearchableSelect'
import PortSelect from '../../../shared/components/PortSelect'

import { getApiErrorMessage, showErrorToast } from '../../../shared/utils/apiErrorMessage';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onVesselAdded: () => void;
}

const extractErrorMessage = (err: unknown, fallback: string): string => {
  return getApiErrorMessage(err, fallback, 'create');
}

const AddVesselModal: React.FC<Props> = ({ onClose, isOpen, onVesselAdded }) => {
    const [selectedCompany, setSelectedCompany] = useState<{ id: number; name: string }>({ id: 0, name: '' });
    const [selectedCompanyAdmin, setSelectedCompanyAdmin] = useState<{ id: number; name: string }>({ id: 0, name: '' });

    const [vesselName, setVesselName] = useState("");
    const [vesselInitials, setVesselInitials] = useState("");
    const [imoNumber, setImoNumber] = useState("");
    const [officialNumber, setOfficialNumber] = useState("");
    const [vesselMMSI, setVesselMMSI] = useState("");
    const [callSign, setCallSign] = useState("");
    const [vesselFlag, setVesselFlag] = useState("");
    const [portOfRegistry, setPortOfRegistry] = useState("");
    const [vesselClass, setVesselClass] = useState("");
    const [classNotation, setClassNotation] = useState("");
    const [piClub, setPiClub] = useState("");
    const [vesselArea, setVesselArea] = useState("");
    const [dwt, setDwt] = useState("");
    const [vesselType, setVesselType] = useState("");
    const [vesselPhoneNumbers, setVesselPhoneNumbers] = useState(buildContactEntries());
    const [vesselEmails, setVesselEmails] = useState(buildContactEntries());
    const [vesselImageFile, setVesselImageFile] = useState<File | null>(null);

    const [naFields, setNaFields] = useState<Record<string, boolean>>({});

    const fieldSetters: Record<string, React.Dispatch<React.SetStateAction<string>>> = {
        vesselName: setVesselName,
        vesselInitials: setVesselInitials,
        imoNumber: setImoNumber,
        vesselMMSI: setVesselMMSI,
        callSign: setCallSign,
        vesselFlag: setVesselFlag,
        portOfRegistry: setPortOfRegistry,
        vesselClass: setVesselClass,
        classNotation: setClassNotation,
        piClub: setPiClub,
        vesselArea: setVesselArea,
        dwt: setDwt,
    };

    const toggleNA = (field: string) => {
        setNaFields(prev => {
            const newValue = !prev[field];
            const setter = fieldSetters[field];
            if (setter) {
                setter(newValue ? "N/A" : "");
            }
            return { ...prev, [field]: newValue };
        });
    };

    const [error, setError] = useState<string | null>(null);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [companiesbyAdmin, setCompaniesByAdmin] = useState<Company[]>([]);
    const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [supportLoading, setSupportLoading] = useState(false);
    const [supportError, setSupportError] = useState<string | null>(null);

    const { currentUser } = useAuth()
    const ownership = useMemo(() => resolveVesselCreateOwnership(currentUser), [currentUser])

    useEffect(() => {
        if (!isOpen) return
        void loadSupportData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, ownership.requiresCompanyGroupSelection, ownership.usesOwnCompanyGroupScope, ownership.companyGroupAdminId])

    useEffect(() => {
        if (!isOpen) return
        if (ownership.requiresCompanyGroupSelection && selectedCompanyAdmin.id) {
            void fetchCompaniesByAdmin(selectedCompanyAdmin.id)
        }
    }, [isOpen, ownership.requiresCompanyGroupSelection, selectedCompanyAdmin.id])

    useEffect(() => {
        if (isOpen) {
            clearData();
        }
    }, [isOpen]);

    const loadSupportData = async () => {
        setSupportLoading(true)
        setSupportError(null)
        try {
            if (ownership.usesOwnCompanyGroupScope && ownership.companyGroupAdminId) {
                await fetchCompanies()
            } else if (ownership.requiresCompanyGroupSelection) {
                await fetchCompanyAdmins()
                setCompanies([])
                setCompaniesByAdmin([])
            }
        } catch (err) {
            const message = extractErrorMessage(err, 'Failed to load company data.')
            setSupportError(message)
        } finally {
            setSupportLoading(false)
        }
    }

    const validateVesselForm = () => {
        const errors: Record<string, string> = {};

        if (!vesselName.trim()) {
            errors.vesselName = "Vessel Name is required.";
        }

        const hasImo = Boolean(imoNumber && imoNumber.trim());
        const hasOfficialNumber = Boolean(officialNumber && officialNumber.trim());

        if (!hasImo && !hasOfficialNumber) {
            errors.imoNumber = "Either IMO Number or Official Number is required.";
            errors.officialNumber = "Either IMO Number or Official Number is required.";
        } else if (hasImo && !/^\d{7}$/.test(imoNumber.trim())) {
            errors.imoNumber = "IMO Number must be a 7-digit number.";
        }

        if (!vesselMMSI || !/^\d{9}$/.test(vesselMMSI)) {
            errors.vesselMMSI = "MMSI must be a 9-digit number.";
        }

        if (!naFields.callSign && (!callSign || !/^[a-zA-Z0-9]{5,10}$/.test(callSign))) {
            errors.callSign = "Call Sign must be between 5 and 10 alphanumeric characters.";
        }

        if (!naFields.vesselFlag && !vesselFlag.trim()) {
            errors.vesselFlag = "Vessel Flag is required.";
        }

        if (!naFields.vesselClass && !vesselClass.trim()) {
            errors.vesselClass = "Vessel Class is required.";
        }

        if (!naFields.vesselArea && !vesselArea.trim()) {
            errors.vesselArea = "Vessel Area is required.";
        }

        if (!naFields.dwt && (!dwt || +dwt <= 0)) {
            errors.dwt = "DWT must be a positive number.";
        }

        if (!vesselType) {
            errors.vesselType = "Vessel Type is required.";
        }

        const phoneError = validateContactEntries(vesselPhoneNumbers, 'phone')
        if (phoneError) {
            errors.vesselPhoneNumber = phoneError
        }
        const emailError = validateContactEntries(vesselEmails, 'email')
        if (emailError) {
            errors.vesselEmailAddress = emailError
        }

        if (ownership.requiresCompanyGroupSelection && selectedCompanyAdmin.id === 0) {
            errors.company = "Company is required.";
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const fetchCompanyAdmins = async () => {
        try {
            const companyList = await getCompanyAdminList();
            const rows = Array.isArray(companyList) ? companyList : []
            setCompanyAdmins(rows.filter(company => company.active === true));
        } catch (err) {
            console.error('Failed to fetch company admin list:', err);
            setCompanyAdmins([]);
            throw err;
        }
    };

    const fetchCompaniesByAdmin = async (adminId: number) => {
        try {
            const companyList = await getCompanyList();
            const rows = Array.isArray(companyList) ? companyList : []
            setCompaniesByAdmin(
                rows.filter((c) => c.cga?.id === adminId && c.active === true)
            );
        } catch (err) {
            console.error('Failed to load sub-companies:', err);
            setCompaniesByAdmin([]);
            showErrorToast(err, 'Failed to load sub-companies for selected company group.');
        }
    };

    const fetchCompanies = async () => {
        try {
            const companyList = await getCompanyList();
            const rows = Array.isArray(companyList) ? companyList : []
            const active = rows.filter(c => c.active === true)

            if (ownership.usesOwnCompanyGroupScope && ownership.companyGroupAdminId) {
                setCompanies(active.filter(c => c.cga?.id === Number(ownership.companyGroupAdminId)))
            } else {
                setCompanies([])
            }
        } catch (err) {
            console.error('Failed to fetch sub-companies:', err);
            setCompanies([]);
            throw err;
        }
    };

    const clearData = () => {
        setSelectedCompany({ id: 0, name: '' });
        setSelectedCompanyAdmin({ id: 0, name: '' });
        setVesselName("");
        setVesselInitials("");
        setImoNumber("");
        setOfficialNumber("");
        setVesselMMSI("");
        setCallSign("");
        setVesselFlag("");
        setPortOfRegistry("");
        setVesselClass("");
        setClassNotation("");
        setPiClub("");
        setVesselArea("");
        setDwt("");
        setVesselType("");
        setVesselPhoneNumbers(buildContactEntries());
        setVesselEmails(buildContactEntries());
        setVesselImageFile(null);
        setNaFields({});
        setFieldErrors({});
        setError(null);
        setSupportError(null);
    };

    const handleAddVessel = async () => {
        if (!validateVesselForm()) return;
        setLoading(true);
        setError(null);
        try {
            const { companyGroupAdminId, companyAdminId } = resolveVesselCreatePayloadIds(
                ownership,
                selectedCompanyAdmin.id,
                selectedCompany.id
            )

            if (!companyGroupAdminId) {
                throw new Error('Company scope is required to create a vessel.')
            }

            const serializedPhoneNumbers = serializeContactEntries(vesselPhoneNumbers)
            const serializedEmails = serializeContactEntries(vesselEmails)

            const createdVessel = await createVessel(
                vesselName,
                vesselType,
                imoNumber,
                officialNumber,
                vesselMMSI,
                callSign,
                vesselFlag,
                portOfRegistry,
                vesselClass,
                classNotation,
                piClub,
                vesselArea,
                dwt,
                getPrimaryContactValue(vesselPhoneNumbers),
                getPrimaryContactValue(vesselEmails),
                serializedPhoneNumbers,
                serializedEmails,
                companyGroupAdminId,
                companyAdminId,
                naFields,
                vesselInitials,
            )

            if (vesselImageFile) {
              try {
                await uploadVesselImage(createdVessel.id, vesselImageFile)
              } catch (imageError) {
                const imageMessage = extractErrorMessage(imageError, 'Vessel was created but image upload failed.')
                toast.warn(imageMessage, {position: 'top-center'})
                onVesselAdded()
                clearData()
                onClose()
                return
              }
            }

            toast.success("Vessel added successfully");
            onVesselAdded();
            clearData();
            onClose();
        } catch (err) {
            console.error('Failed to add vessel:', err);
            const message = extractErrorMessage(err, 'Failed to add vessel.')
            setError(message);
            toast.error(message, {position: 'top-center'});
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '60rem', overflowY: "scroll" }}>
                <div className="custom-modal-header d-flex justify-content-between align-items-center">
                    <h5 className="m-0">Add Vessel</h5>
                    <button type="button" className="close-btn" onClick={onClose} aria-label="Close">
                        <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
                    </button>
                </div>

                <div className="custom-modal-body">
                    {error && <div className="alert alert-danger">{error}</div>}
                    {supportLoading && (
                      <div className="alert alert-info py-2">Loading company data…</div>
                    )}
                    {supportError && (
                      <div className="alert alert-warning">{supportError}</div>
                    )}

                    <div className="row">
                        {ownership.showSubCompanyPicker && ownership.usesOwnCompanyGroupScope && (
  <div className="col-md-6 mb-3">
    <label className="modal_label" htmlFor="add-vessel-sub-company">Select Sub Company</label>
    <SearchableSelect
      id="add-vessel-sub-company"
      className="form-control"
      value={selectedCompany?.id || ''}
      onChange={(e) => {
        const id = +e.target.value;
        const obj = companies.find(c => c.id === id);
        setSelectedCompany(obj || { id: 0, name: '' });
      }}
    >
      <option value="">-- Select Sub Company --</option>
      {companies.map(c => (
        <option key={c.id} value={c.id}>{c.name}</option>
      ))}
    </SearchableSelect>
  </div>
)}
                        {ownership.requiresCompanyGroupSelection && (
  <>
    <div className="col-md-6 mb-3">
      <label className="modal_label" htmlFor="add-vessel-company">Select Company <span className="text-danger">*</span></label>
      <SearchableSelect
        id="add-vessel-company"
        className="form-control"
        value={selectedCompanyAdmin?.id || ''}
        onChange={(e) => {
          const id = +e.target.value;
          const obj = companyAdmins.find(c => c.id === id);
          setSelectedCompanyAdmin(obj || { id: 0, name: '' });
          setSelectedCompany({ id: 0, name: '' });
          if (id) {
            void fetchCompaniesByAdmin(id);
          } else {
            setCompaniesByAdmin([]);
          }
        }}
      >
        <option value="">-- Select Company --</option>
        {companyAdmins.map(ca => (
          <option key={ca.id} value={ca.id}>{ca.name}</option>
        ))}
      </SearchableSelect>
      {fieldErrors.company && <small className="text-danger">{fieldErrors.company}</small>}
    </div>

    <div className="col-md-6 mb-3">
      <label className="modal_label" htmlFor="add-vessel-sub-company-admin">Select Sub Company</label>
      <SearchableSelect
        id="add-vessel-sub-company-admin"
        className="form-control"
        value={selectedCompany?.id || ''}
        onChange={(e) => {
          const id = +e.target.value;
          const obj = companiesbyAdmin.find(c => c.id === id);
          setSelectedCompany(obj || { id: 0, name: '' });
        }}
      >
        <option value="">-- Select Sub Company --</option>
        {companiesbyAdmin.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </SearchableSelect>
    </div>
  </>
)}
                        <div className="col-md-6 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-name">
                                Vessel Name <span className="text-danger">*</span>
                            </label>
                            <input id="add-vessel-name" type="text" className="form-control" value={vesselName} onChange={(e) => setVesselName(e.target.value)} />
                            {fieldErrors.vesselName && <small className="text-danger">{fieldErrors.vesselName}</small>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-initials">
                                Vessel Initials
                                <span className="float-end">
                                    <input type="checkbox" className="form-check-input me-1" checked={naFields.vesselInitials || false} onChange={() => toggleNA('vesselInitials')} />
                                    <small>N/A</small>
                                </span>
                            </label>
                            {naFields.vesselInitials ? (
                                <input id="add-vessel-initials" type="text" className="form-control" value="N/A" disabled />
                            ) : (
                                <input
                                    id="add-vessel-initials"
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. MV"
                                    maxLength={20}
                                    value={vesselInitials}
                                    onChange={(e) => setVesselInitials(e.target.value)}
                                />
                            )}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-imo">
                                IMO Number
                            </label>
                            <input id="add-vessel-imo" type="number" className="form-control no-spinner" value={imoNumber} onChange={(e) => {
                                setImoNumber(e.target.value)
                            }} />
                            {fieldErrors.imoNumber && <small className="text-danger">{fieldErrors.imoNumber}</small>}
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-official-number">
                                Official Number
                            </label>
                            <input id="add-vessel-official-number" type="text" className="form-control" value={officialNumber} onChange={(e) => setOfficialNumber(e.target.value)} />
                            {fieldErrors.officialNumber && <small className="text-danger">{fieldErrors.officialNumber}</small>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-mmsi">
                                Vessel MMSI <span className="text-danger">*</span>
                            </label>
                            <input id="add-vessel-mmsi" type="number" className="form-control no-spinner" value={vesselMMSI} onChange={(e) => {
                                setVesselMMSI(e.target.value);
                            }} />
                            {fieldErrors.vesselMMSI && <small className="text-danger">{fieldErrors.vesselMMSI}</small>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-call-sign">
                                Vessel CALL SIGN {!naFields.callSign && <span className="text-danger">*</span>}
                                <span className="float-end">
                                    <input type="checkbox" className="form-check-input me-1" checked={naFields.callSign || false} onChange={() => toggleNA('callSign')} />
                                    <small>N/A</small>
                                </span>
                            </label>
                            {naFields.callSign ? (
                                <input id="add-vessel-call-sign" type="text" className="form-control" value="N/A" disabled />
                            ) : (
                                <input id="add-vessel-call-sign" type="text" className="form-control" value={callSign} onChange={(e) => {
                                    setCallSign(e.target.value)
                                }} />
                            )}
                            {fieldErrors.callSign && <small className="text-danger">{fieldErrors.callSign}</small>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-flag">
                                Vessel FLAG {!naFields.vesselFlag && <span className="text-danger">*</span>}
                                <span className="float-end">
                                    <input type="checkbox" className="form-check-input me-1" checked={naFields.vesselFlag || false} onChange={() => toggleNA('vesselFlag')} />
                                    <small>N/A</small>
                                </span>
                            </label>
                            {naFields.vesselFlag ? (
                                <input id="add-vessel-flag" type="text" className="form-control" value="N/A" disabled />
                            ) : (
                                <input id="add-vessel-flag" type="text" className="form-control" value={vesselFlag} onChange={(e) => setVesselFlag(e.target.value)} />
                            )}
                            {fieldErrors.vesselFlag && <small className="text-danger">{fieldErrors.vesselFlag}</small>}
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-port">
                                Port of Registry
                                <span className="float-end">
                                    <input type="checkbox" className="form-check-input me-1" checked={naFields.portOfRegistry || false} onChange={() => toggleNA('portOfRegistry')} />
                                    <small>N/A</small>
                                </span>
                            </label>
                            {naFields.portOfRegistry ? (
                                <input id="add-vessel-port" type="text" className="form-control" value="N/A" disabled />
                            ) : (
                                <PortSelect
                                    id="add-vessel-port"
                                    value={portOfRegistry}
                                    onChange={(e) => setPortOfRegistry(e.target.value)}
                                    placeholder="Select or search port of registry..."
                                />
                            )}
                        </div>

                        <div className="col-md-12 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-class">
                                Vessel CLASS {!naFields.vesselClass && <span className="text-danger">*</span>}
                                <span className="float-end">
                                    <input type="checkbox" className="form-check-input me-1" checked={naFields.vesselClass || false} onChange={() => toggleNA('vesselClass')} />
                                    <small>N/A</small>
                                </span>
                            </label>
                            {naFields.vesselClass ? (
                                <textarea id="add-vessel-class" className="form-control" rows={3} value="N/A" disabled />
                            ) : (
                                <textarea id="add-vessel-class" className="form-control" rows={3} value={vesselClass} onChange={(e) => setVesselClass(e.target.value)} />
                            )}
                            {fieldErrors.vesselClass && <small className="text-danger">{fieldErrors.vesselClass}</small>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-area">
                                Vessel Area (A1, A2, A3, A4) {!naFields.vesselArea && <span className="text-danger">*</span>}
                                <span className="float-end">
                                    <input type="checkbox" className="form-check-input me-1" checked={naFields.vesselArea || false} onChange={() => toggleNA('vesselArea')} />
                                    <small>N/A</small>
                                </span>
                            </label>
                            {naFields.vesselArea ? (
                                <input id="add-vessel-area" type="text" className="form-control" value="N/A" disabled />
                            ) : (
                                <input id="add-vessel-area" type="text" className="form-control" value={vesselArea} onChange={(e) => setVesselArea(e.target.value)} />
                            )}
                            {fieldErrors.vesselArea && <small className="text-danger">{fieldErrors.vesselArea}</small>}
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-class-notation">
                                Class Notation
                                <span className="float-end">
                                    <input type="checkbox" className="form-check-input me-1" checked={naFields.classNotation || false} onChange={() => toggleNA('classNotation')} />
                                    <small>N/A</small>
                                </span>
                            </label>
                            {naFields.classNotation ? (
                                <input id="add-vessel-class-notation" type="text" className="form-control" value="N/A" disabled />
                            ) : (
                                <input id="add-vessel-class-notation" type="text" className="form-control" value={classNotation} onChange={(e) => setClassNotation(e.target.value)} />
                            )}
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-pi-club">
                                P&amp;I Club
                                <span className="float-end">
                                    <input type="checkbox" className="form-check-input me-1" checked={naFields.piClub || false} onChange={() => toggleNA('piClub')} />
                                    <small>N/A</small>
                                </span>
                            </label>
                            {naFields.piClub ? (
                                <input id="add-vessel-pi-club" type="text" className="form-control" value="N/A" disabled />
                            ) : (
                                <input id="add-vessel-pi-club" type="text" className="form-control" value={piClub} onChange={(e) => setPiClub(e.target.value)} />
                            )}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-dwt">
                                DWT {!naFields.dwt && <span className="text-danger">*</span>}
                                <span className="float-end">
                                    <input type="checkbox" className="form-check-input me-1" checked={naFields.dwt || false} onChange={() => toggleNA('dwt')} />
                                    <small>N/A</small>
                                </span>
                            </label>
                            {naFields.dwt ? (
                                <input id="add-vessel-dwt" type="text" className="form-control" value="N/A" disabled />
                            ) : (
                                <input id="add-vessel-dwt" type="number" className="form-control no-spinner" value={dwt} onChange={(e) => setDwt(e.target.value)} />
                            )}
                            {fieldErrors.dwt && <small className="text-danger">{fieldErrors.dwt}</small>}
                        </div>
                        <div className="col-md-6 mb-3">
                            <VesselContactListField
                                addLabel="Add Phone Number"
                                entries={vesselPhoneNumbers}
                                error={fieldErrors.vesselPhoneNumber}
                                idPrefix="add-vessel-phone"
                                inputType="text"
                                label="Vessel Phone Numbers"
                                placeholder="Enter vessel phone number"
                                onAdd={() => setVesselPhoneNumbers((current) => addContactEntry(current))}
                                onChange={(index, value) => setVesselPhoneNumbers((current) => updateContactValue(current, index, value))}
                                onMakePrimary={(index) => setVesselPhoneNumbers((current) => setPrimaryContact(current, index))}
                                onRemove={(index) => setVesselPhoneNumbers((current) => removeContactEntry(current, index))}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <VesselContactListField
                                addLabel="Add Email Address"
                                entries={vesselEmails}
                                error={fieldErrors.vesselEmailAddress}
                                idPrefix="add-vessel-email"
                                inputType="email"
                                label="Vessel Email Addresses"
                                placeholder="Enter vessel email address"
                                onAdd={() => setVesselEmails((current) => addContactEntry(current))}
                                onChange={(index, value) => setVesselEmails((current) => updateContactValue(current, index, value))}
                                onMakePrimary={(index) => setVesselEmails((current) => setPrimaryContact(current, index))}
                                onRemove={(index) => setVesselEmails((current) => removeContactEntry(current, index))}
                            />
                        </div>

                        <div className="col-md-12 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-type">Vessel Type <span className="text-danger">*</span></label>
                            <SearchableSelect id="add-vessel-type" className="form-control" value={vesselType} onChange={(e) => {
                                setVesselType(e.target.value)
                            }}>
                                <option value="">-- Select Vessel Type --</option>
                                {VESSEL_TYPE_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </SearchableSelect>
                            {fieldErrors.vesselType && <small className="text-danger">{fieldErrors.vesselType}</small>}
                        </div>
                        <div className="col-md-12 mb-3">
                            <label className="modal_label" htmlFor="add-vessel-image">Vessel Image</label>
                            <input
                              id="add-vessel-image"
                              type="file"
                              className="form-control"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              onChange={(e) => setVesselImageFile(e.target.files?.[0] || null)}
                            />
                            <small className="text-muted">Optional. Allowed: PNG, JPG, JPEG, WEBP</small>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn_secondary" onClick={onClose} disabled={loading}>Cancel</button>
                    <button type="button" className="btn btn_success d-flex align-items-center gap-2" onClick={handleAddVessel} disabled={loading}>
                      {loading && <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />}
                      {loading ? 'Adding...' : 'Add'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddVesselModal;
